import { createFileRoute, Link } from "@tanstack/react-router";
import {
	Bot,
	CalendarClock,
	GitBranch,
	MessageSquare,
	ShieldCheck,
	Workflow,
} from "lucide-react";
import {
	type ComparisonRow,
	ComparisonTable,
} from "@/components/comparison-table";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/alternatives/hootsuite";

export const Route = createFileRoute(path)({
	component: HootsuiteAlternative,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title:
				"AgentReacher | Hootsuite Alternative for AI Social Media Scheduling",
			description:
				"A Hootsuite alternative that pairs scheduling with an AI assistant. Cross-post, approve, and publish across every network from one chat.",
		}),
});

const comparison: ComparisonRow[] = [
	{
		feature: "AI agent workflow",
		competitor: "Add-on",
		agentreacher: "Native",
	},
	{ feature: "MCP integration", competitor: false, agentreacher: true },
	{ feature: "Bluesky support", competitor: false, agentreacher: true },
	{ feature: "Threads support", competitor: "Limited", agentreacher: true },
	{
		feature: "Approval flow",
		competitor: "Paid tier",
		agentreacher: "Built in",
	},
	{ feature: "Cross-posting", competitor: true, agentreacher: true },
	{
		feature: "Starting plan",
		competitor: "From €99/mo",
		agentreacher: "From €19/mo",
	},
];

function HootsuiteAlternative() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Hootsuite alternative"
				title="A Hootsuite alternative without the enterprise price tag."
				description="AgentReacher pairs scheduling with an AI assistant. Skip the heavy editor and drive every channel from chat."
			/>

			<MarketingSection
				title="Why teams move off Hootsuite"
				description="Hootsuite was designed before AI assistants existed. AgentReacher is built around them."
			>
				<FeatureGrid
					features={[
						{
							icon: Bot,
							title: "Native AI workflow",
							description:
								"Drafts, scheduling, and publishing run from your agent, not as a paid add-on layer.",
						},
						{
							icon: Workflow,
							title: "MCP for Claude and ChatGPT",
							description:
								"Plug your assistant in and run the whole calendar without opening the dashboard.",
						},
						{
							icon: GitBranch,
							title: "Bluesky and Threads",
							description:
								"Coverage of the networks Hootsuite still treats as second-class, alongside the rest.",
						},
						{
							icon: CalendarClock,
							title: "One unified calendar",
							description:
								"Every queued post lives in one view. Edits propagate cleanly across networks.",
						},
						{
							icon: ShieldCheck,
							title: "Approvals on every plan",
							description:
								"Reviewer roles ship without paying enterprise fees per seat.",
						},
						{
							icon: MessageSquare,
							title: "Honest pricing",
							description:
								"€19/mo to start. No hidden tiers or per-channel multipliers.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="AgentReacher vs. Hootsuite">
				<ComparisonTable competitorName="Hootsuite" rows={comparison} />
			</MarketingSection>

			<MarketingSection title="What changes day to day">
				<div className="mx-auto max-w-2xl space-y-4 text-sm leading-relaxed text-muted-foreground">
					<p>
						In Hootsuite, a typical week starts with opening the planner,
						writing each post by hand, picking time slots, and saving variations
						per channel. With AgentReacher, you describe the week in chat. The
						agent drafts the queue, you review on the calendar, and anything
						that needs tweaking happens inline. Publishing handles platform
						specifics, retries, and scheduling.
					</p>
					<p>
						See the{" "}
						<Link
							to="/blog"
							className="text-primary transition-colors hover:text-primary/80"
						>
							latest blog guides
						</Link>{" "}
						or jump into{" "}
						<Link
							to="/pricing"
							className="text-primary transition-colors hover:text-primary/80"
						>
							pricing
						</Link>
						.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
